import {
  getCookie,
  getSessionData,
  setCookie,
  UserSessionDataTypes,
  setAppCampaignCookie,
} from "@repo/shared-utils/common-utils";
import {
  AIRTEL,
  AUTH_TYPE,
  CHECK_COOKIE,
  EXPIRED_TIME,
  KEY,
  LSFLAG,
  PARTNER_DATA,
  PERPETUAL_TOKEN,
  REQ_DATA,
  TOKEN,
  TOKEN_EXPIRY,
  TOKEN_SRC,
} from "../constants/sso.constants";
import {
  decrypt,
  decryptFederated,
  decryptLocalStorageMobileNo,
  decryptWebform,
  encryptLocalStorageMobileNo,
} from "@repo/shared-utils/crypto";
import {
  debounce,
  getCustomerDemogFromApp,
  getParams,
  isValidJSON,
  PerpetualSessionDataType,
  removeQueryParam,
  setSessionData,
  setSessionDataCookie,
  throttle,
  updateCleverTapPageObject,
} from "./utils";
import { verifySSOToken } from "@repo/connection-manager";
import { clearPerpetualSession, clearSession, loginUser, logoutUser, setUserDetails } from "./user";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { federatedBflWebAuthorize } from "../services/federatedBflWebLogin";
import { federatedAuthorize } from "../services/federatedApi";
import dayjs, { type Dayjs } from "dayjs";
import { setLoginStatus, store } from "@repo/global-store";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  COMMON_SSO_ACCESS_TOKEN,
  APP_3in1,
  SOURCE,
  APP_SRC,
  APP_M1,
  WEB_VIEW_MODE,
  WEB_VIEW_MODE_ON,
} from "@repo/shared-utils/sso.constants";

/**
 * Calculates the token expiry time based on the CHECK_COOKIE value
 * @returns {Dayjs} The calculated token expiry time (current time + 10 minutes)
 */
export const getTokenExpiryTime = () => {
  const checkCookie = Number.parseFloat(getCookie(CHECK_COOKIE) ?? "0");
  const tokenExpiryTime = dayjs(checkCookie * 1000).add(10, "minutes");
  return tokenExpiryTime;
};

/**
 * Validates an access token based on specific criteria
 * @param {string} token - The token to validate
 * @returns {boolean} Whether the token is valid
 */
export const validateAccessToken = (token: string): boolean => {
  if (!token || typeof token !== "string") {
    logErrorToNewRelic("SSO: Token validation failed - invalid token type", {
      function: "validateAccessToken",
      action: "validation_failed",
      reason: "token_is_empty_or_not_string",
      tokenType: typeof token,
      hasToken: !!token,
      tokenValue: token,
      appVersion: ((window as any).tarsier?.invokePrompt(APP_PROMPTS.APP_VERSION) as string) ?? "",
      pageLink: (window as any).location.href,
      timestamp: new Date().toISOString(),
    });
    return false;
  }

  // Check minimum length requirement (57 characters)
  if (token.length < 57) {
    logErrorToNewRelic("SSO: Token validation failed - insufficient length", {
      function: "validateAccessToken",
      action: "validation_failed",
      reason: "token_length_insufficient",
      tokenLength: token.length,
      tokenValue: token,
      appVersion: ((window as any).tarsier?.invokePrompt(APP_PROMPTS.APP_VERSION) as string) ?? "",
      pageLink: (window as any).location.href,
      timestamp: new Date().toISOString(),
    });
    return false;
  }

  // Check if token starts with 14 digits (timestamp pattern)
  const timestampPattern = /^\d{14}/;
  if (!timestampPattern.test(token)) {
    logErrorToNewRelic("SSO: Token validation failed - invalid timestamp pattern", {
      function: "validateAccessToken",
      action: "validation_failed",
      reason: "invalid_timestamp_pattern",
      tokenLength: token.length,
      tokenValue: token,
      appVersion: ((window as any).tarsier?.invokePrompt(APP_PROMPTS.APP_VERSION) as string) ?? "",
      pageLink: (window as any).location.href,
      timestamp: new Date().toISOString(),
    });
    return false;
  }

  return true;
};

/**
 * Sets up a user session with the provided token and mobile number
 * @param {string} token - The authentication token to use
 * @param {string} mobileNo - The mobile number of the user
 * @returns {Promise<void>}
 */
export const setupSessionWithToken = async (token: string, mobileNo: string) => {
  localStorage.setItem(COMMON_SSO_ACCESS_TOKEN, token);
  setCookie(TOKEN_SRC, 0, APP_3in1);
  setCookie(LSFLAG, 0, "true");
  await setSessionData("mobileNo", mobileNo);
  await initAppLoginSession(mobileNo);
};

/**
 * Refreshes the authentication token before it expires
 * @param {Dayjs} tokenExpiryTime - The expiry time of the current token
 * @param {string} mobileNo - The mobile number associated with the token
 * @returns {Promise<void>} A promise that resolves when the token is refreshed or rejects on failure
 */
/* istanbul ignore next */
const refreshToken = async (tokenExpiryTime: Dayjs, mobileNo: string) =>
  new Promise<void>((resolve, reject) => {
    const currentTime = dayjs();
    const secondsToRefresh = tokenExpiryTime.diff(currentTime, "seconds");
    const interval = secondsToRefresh / 7 + 1;
    const intervalId = setInterval(async () => {
      const nextRunTime = dayjs().add(interval, "seconds");
      if (nextRunTime.isAfter(tokenExpiryTime)) {
        try {
          await verifySSOToken(mobileNo);
          clearInterval(intervalId);
          resolve();
        } catch (error) {
          clearInterval(intervalId);
          reject(error);
        }
      }
    }, interval * 1000);
  });

/**
 * Initializes the automatic token refresh process
 * @param {Dayjs} tokenExpiryTime - The initial token expiry time
 * @returns {Promise<void>}
 */
/* istanbul ignore next */
const initAutoRefreshToken = async (tokenExpiryTime: Dayjs) => {
  const { mobileNo } = await getSessionData();
  while (true) {
    try {
      await refreshToken(tokenExpiryTime, mobileNo);
      // biome-ignore lint/style/noParameterAssign: This is a valid use case
      tokenExpiryTime = getTokenExpiryTime();
      localStorage.setItem(TOKEN_EXPIRY, tokenExpiryTime.valueOf().toString());
    } catch (error) {
      logoutUser(mobileNo, { withPerpetual: false });
      break;
    }
  }
};

/**
 * Initializes the idle timeout mechanism that logs out users after a period of inactivity
 * @param {Dayjs} idleTimeoutTime - The time at which the user should be logged out if idle
 * @returns {Promise<void>}
 */
export const initIdleTimeout = async (idleTimeoutTime: Dayjs) => {
  const millisecondsToIdleTimeout = idleTimeoutTime.diff(dayjs(), "milliseconds");
  const { mobileNo } = await getSessionData();
  const debouncedLogout = debounce(async () => {
    logoutUser(mobileNo, { withPerpetual: false });
    store.dispatch(setLoginStatus({ loginStatus: "perpetual" }));
  }, millisecondsToIdleTimeout);
  debouncedLogout();
  const removeActivityListener = addActivityListener(
    throttle(() => {
      const idleTimeoutTime = dayjs().add(15, "minutes");
      localStorage.setItem(EXPIRED_TIME, idleTimeoutTime.valueOf().toString());
      const millisecondsToIdleTimeout = idleTimeoutTime.diff(dayjs(), "milliseconds");
      debouncedLogout(millisecondsToIdleTimeout, removeActivityListener);
    }, 30 * 1000)
  );
};

/**
 * Retrieves the user's mobile number from storage (sessionStorage or localStorage)
 * Priority: sessionStorage (current session) → localStorage (perpetual) → session data
 * @returns {Promise<string|null>} The mobile number if found, null otherwise
 */
export const getMobileNumberFromStorage = async (): Promise<string | null> => {
  // Try sessionStorage first (for current session flows like sticky CTA) - more secure, non-persistent
  const sessionMobileNumber = sessionStorage.getItem("mobileNumber");
  if (sessionMobileNumber) {
    try {
      // Keep backward compatibility with legacy numeric values while supporting encrypted values.
      const numericValue = Number(sessionMobileNumber);
      return Number.isNaN(numericValue)
        ? await decryptLocalStorageMobileNo(sessionMobileNumber)
        : sessionMobileNumber;
    } catch (error) {
      console.warn("Failed to decrypt mobile number from sessionStorage:", error);
    }
  }

  // Try from localStorage (for perpetual logins)
  const storedMobileNumber = localStorage.getItem("mobileNumber");
  if (storedMobileNumber) {
    try {
      // Check if the stored value is encrypted (non-numeric)
      const numericValue = Number(storedMobileNumber);
      return Number.isNaN(numericValue)
        ? await decryptLocalStorageMobileNo(storedMobileNumber)
        : storedMobileNumber;
    } catch (error) {
      console.warn("Failed to decrypt mobile number from localStorage:", error);
    }
  }

  // Fallback to session data
  try {
    const { mobileNo } = await getSessionData();
    return mobileNo || null;
  } catch (error) {
    console.warn("Failed to get mobile number from session:", error);
    return null;
  }
};

type GlobalEventNames = keyof GlobalEventHandlersEventMap;

/**
 * Adds activity listeners to track user activity and reset idle timeout
 * @param {Function} whenActive - Callback function to execute when user activity is detected
 * @returns {Function} Function to remove all event listeners
 */
const addActivityListener = (whenActive: () => void) => {
  const events: GlobalEventNames[] = [
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
  ];
  for (const event of events) {
    (window as any).addEventListener(event, whenActive, true);
  }
  return function removeActivityListener() {
    for (const event of events) {
      (window as any).removeEventListener(event, whenActive, true);
    }
  };
};

// IMPLEMENTATION
/**
 * Global promise for token management with retry logic.
 *
 * This function implements the following flow:
 * 1. Hard refresh with delay of 0.75s
 * 2. If token not found, retry up to 2 times with delay of 0.5s
 * 3. If token found: resolve with setIsTokenPresent(true) and setup session
 * 4. If max retries reached: reject with setIsTokenPresent(false)
 *
 * @param mobileNo - The mobile number for the user session
 * @param maxRetries - Maximum number of retry attempts (default: 2)
 * @returns Promise that resolves when token is found and session is setup, rejects when max retries reached
 */
// Global promise for token management with retry logic
/**
 * Global promise for token management with retry logic
 * This function implements the following flow:
 * 1. Hard refresh with delay of 0.75s
 * 2. If token not found, retry up to maxRetries times with delay of 0.5s
 * 3. If token found: resolve with session setup
 * 4. If max retries reached: reject
 *
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @returns {Promise<void>} Promise that resolves when token is found and session is setup, rejects when max retries reached
 */
export const getTokenWithRetry = async (maxRetries = 2): Promise<void> => {
  const keyParam = getParams(KEY);
  const key = keyParam ? decodeURIComponent(keyParam) : (getCustomerDemogFromApp()?.mobileNo ?? "");
  const mobileNo = keyParam ? await decryptFederated(key) : key;
  const encryptMobileNumber = await encryptLocalStorageMobileNo(mobileNo);

  return new Promise((resolve, reject) => {
    let retryCount = 0;

    const attemptTokenRetrieval = async (): Promise<void> => {
      try {
        // Hard refresh with delay of 0.75s
        (window as any).tarsier?.invokePrompt(APP_PROMPTS.HARD_REFRESH_APP_TOKEN, true);
        await new Promise((resolve) => setTimeout(resolve, 750));

        // Get token from app
        const token = (window as any).tarsier?.invokePrompt(
          APP_PROMPTS.GET_APP_TOKEN,
          true
        ) as string;

        if (token && token.length > 0 && validateAccessToken(token)) {
          // Token found - resolve with success actions
          await setupSessionWithToken(token, mobileNo);
          resolve();
          return;
        }

        throw new Error("Token not found or empty");
      } catch (error) {
        retryCount++;

        if (retryCount <= maxRetries) {
          // Retry with 0.5s delay
          logErrorToNewRelic("SSO: Token retry attempt", {
            function: "getTokenWithRetry",
            action: "retry_attempt",
            retryCount,
            maxRetries,
            mobileNo: encryptMobileNumber || "noMobileNo",
            error: (error as Error).message,
            timestamp: new Date().toISOString(),
            pageLink: (window as any).location.href,
          });

          setTimeout(attemptTokenRetrieval, 500);
        } else {
          // Max retries reached - reject with failure actions
          store.dispatch(setLoginStatus({ loginStatus: "failure" }));
          logErrorToNewRelic("SSO: getTokenWithRetry max retries reached", {
            function: "getTokenWithRetry",
            action: "max_retries_reached",
            retryCount,
            maxRetries,
            mobileNo: mobileNo || "noMobileNo",
            error: (error as Error).message,
            timestamp: new Date().toISOString(),
            pageLink: (window as any).location.href,
          });

          reject(
            new Error(
              `Token retrieval failed after ${maxRetries} retries: ${(error as Error).message}`
            )
          );
        }
      }
    };

    // Start the first attempt
    attemptTokenRetrieval();
  });
};

/**
 * Initializes the app login session for a user
 * @param {string} mobileNo - The mobile number of the user
 * @returns {Promise<void>}
 */
export const initAppLoginSession = async (mobileNo: string) => {
  await setUserDetails(mobileNo);
  store.dispatch(setLoginStatus({ loginStatus: "success" }));

  if ((window as any).clevertap) {
    updateCleverTapPageObject({
      EP_LOGIN_STATUS: "logged-in",
    });
    (window as any).clevertap?.onUserLogin?.push({
      Site: {
        Identity: mobileNo,
      },
    });
  }
  setAppCampaignCookie();
};

/**
 * Initializes a login session with all required setup
 * @param {string} mobileNo - The mobile number of the user
 * @returns {Promise<void>}
 */
export const initLoginSession = async (mobileNo: string) => {
  await setSessionData("mobileNo", mobileNo);
  await setUserDetails(mobileNo);
  store.dispatch(setLoginStatus({ loginStatus: "success" }));
  // this can be removed in the future, when react project is removed
  setCookie(LSFLAG, 0, "true");
  // INFO: If no authType cookie found, set it to I
  const authTypeCookie = getCookie(AUTH_TYPE);
  if (!authTypeCookie) {
    setCookie(AUTH_TYPE, 0, "I");
  }
  const tokenExpiryTime = getTokenExpiryTime();
  const idleTimeoutTime = dayjs().add(15, "minutes");
  // this can be removed in the future, when react project is removed
  localStorage.setItem(TOKEN_EXPIRY, tokenExpiryTime.valueOf().toString());
  // this can be removed in the future, when react project is removed
  localStorage.setItem(EXPIRED_TIME, idleTimeoutTime.valueOf().toString());
  // initAutoRefreshToken(tokenExpiryTime);
  // initIdleTimeout(idleTimeoutTime);

  // set clevertap object to window for tracking
  if ((window as any).clevertap) {
    (window as any).clevertap.ctPageObject = {
      ...(window as any).clevertap.ctPageObject,
      EP_LOGIN_STATUS: "logged-in",
    };
    (window as any).clevertap?.onUserLogin?.push({
      Site: {
        Identity: mobileNo,
      },
    });
  }
};

/**
 * Verifies a federated access token and sets up a session if valid
 * @returns {Promise<void>}
 */
export const verifyAccessTokenFederated = async () => {
  try {
    const federatedKeyParam = getParams(KEY) ?? "";
    const federatedToken = getParams(TOKEN) ?? "";
    const federatedKey = await decryptFederated(federatedKeyParam);
    const [mobileNo] = federatedKey.split("|");
    if (mobileNo) {
      await federatedAuthorize(mobileNo, federatedToken);
      initLoginSession(mobileNo);
    }
  } catch (error) {
    removeQueryParam((window as any).location.href, "token");
    removeQueryParam((window as any).location.href, "key");
    const { mobileNo } = await getSessionData();
    logoutUser(mobileNo, { withPerpetual: false });
  }
};

/**
 * Verifies a federated key parameter and logs in the user if valid
 * @returns {Promise<void>}
 */
export const verifyAccessTokenFederatedKeyParam = async () => {
  try {
    const federatedKeyParam = getParams(KEY) ?? "";
    const federatedKey = await decryptFederated(federatedKeyParam);
    const [mobileNo] = federatedKey.split("|");
    await loginUser(mobileNo);
  } catch (error) {
    const { mobileNo } = await getSessionData();
    logoutUser(mobileNo, { withPerpetual: false });
  }
};

/**
 * Verifies the perpetual token and handles perpetual login session
 * @returns {Promise<void>}
 */
export const verifyPerpetualToken = async () => {
  let { mobileNo } = await getSessionData();
  let p_details_json: PerpetualSessionDataType | null = null;

  // p_token is present but mobileNo is empty in userDetails.
  // This happens when user goes to perpetual session on ui.frontend code.
  // We need to get mobileNo from p_details cookie.
  if (!mobileNo || !mobileNo.length) {
    const p_details = getCookie("p_details");
    if (p_details && isValidJSON(p_details)) {
      p_details_json = JSON.parse(p_details) as PerpetualSessionDataType;
      mobileNo = (await decrypt(p_details_json.mobileNo, p_details_json.newDate)) as string;
    }
  }
  const p_token = getCookie(PERPETUAL_TOKEN) || undefined;
  try {
    const { age } = await verifySSOToken(mobileNo, p_token);
    if (age) {
      await logoutUser(mobileNo, { withPerpetual: false, withReload: false });
      if (p_details_json) setSessionDataCookie(p_details_json as UserSessionDataTypes);
      else {
        const sessionData = await getSessionData();
        setCookie("p_details", 30 * 24 * 60, JSON.stringify(sessionData));
      }
      store.dispatch(setLoginStatus({ loginStatus: "perpetual" }));
    } else {
      await logoutUser(mobileNo);
    }
  } catch (error) {
    await logoutUser(mobileNo);
  }
};

/**
 * Sets the app access token based on URL parameters or app prompts
 * @returns {Promise<void>}
 */
export const setAppAccessToken = async () => {
  try {
    const reqData = getParams(REQ_DATA);
    const source = getParams(SOURCE);
    if (source === AIRTEL) {
      reqData && handleReqData(reqData);
      return;
    }

    const tokenParam = getParams(TOKEN);
    const keyParam = getParams(KEY);
    const appToken = (window as any).tarsier?.invokePrompt(
      APP_PROMPTS.GET_APP_TOKEN,
      true
    ) as string;

    // take accessToken from URL param or appToken prompt
    const token = tokenParam || appToken;
    const key = keyParam
      ? decodeURIComponent(keyParam)
      : (getCustomerDemogFromApp()?.mobileNo ?? "");
    const mobileNo = keyParam ? await decryptFederated(key) : key;
    const encryptMobileNumber = await encryptLocalStorageMobileNo(mobileNo);

    const src = getParams(APP_SRC);
    (src !== APP_M1 || source !== AIRTEL) && setCookie(WEB_VIEW_MODE, 0, WEB_VIEW_MODE_ON);

    if (src === APP_M1) {
      setCookie(TOKEN_SRC, 0, APP_M1);
      const ism1authorise = federatedBflWebAuthorize(mobileNo, token, "m1app");
      const statusCode = (await ism1authorise).statusCode;

      if (statusCode === 90) {
        setSessionData("mobileNo", mobileNo);
        localStorage.setItem(COMMON_SSO_ACCESS_TOKEN, token);
        initAppLoginSession(mobileNo);
      }
    } else if (src !== APP_M1) {
      // Check if we already have a valid token, if so use it directly
      if (appToken && appToken.length > 0 && validateAccessToken(appToken)) {
        await setupSessionWithToken(token, mobileNo);
      } else {
        // No token available, use retry mechanism
        try {
          await getTokenWithRetry();
        } catch (error) {
          logErrorToNewRelic("SSO: getTokenWithRetry failed in setAppAccessToken", {
            function: "setAppAccessToken",
            action: "getTokenWithRetry_failed",
            mobileNo: encryptMobileNumber || "noMobileNo",
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString(),
            pageLink: (window as any).location.href,
          });
        }
      }
    }
  } catch (error) {
    logErrorToNewRelic("SSO: Error in setAppAccessToken", {
      function: "setAppAccessToken",
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack || "no stack" : "no stack",
      url: (window as any).location.href,
      timestamp: new Date().toISOString(),
      pageLink: (window as any).location.href,
    });
    throw error;
  }
};

/**
 * Handles encrypted request data from URL parameters
 * @param {string} reqData - The encrypted request data to process
 * @returns {Promise<void>}
 * @throws {Error} If an error occurs during processing
 */
export const handleReqData = async (reqData: string) => {
  try {
    const decryptedReqData = await decryptWebform(reqData);
    if (decryptedReqData === "") {
      store.dispatch(setLoginStatus({ loginStatus: "failure" }));
      return;
    }
    const data = JSON.parse(decryptedReqData);
    const { mobileNo, accessToken } = data.ssoRequest.sso;
    await setSessionData("mobileNo", mobileNo);
    localStorage.setItem(COMMON_SSO_ACCESS_TOKEN, accessToken);
    await verifySSOToken(mobileNo);
    initLoginSession(mobileNo);
    if (isOpenInApp()) {
      initAppLoginSession(mobileNo);
    }
    const partnerData = {
      partner_authdetails: data.ssoRequest.sso.partner_authdetails,
      utm: data.ssoRequest.utm,
    };
    sessionStorage.setItem(PARTNER_DATA, JSON.stringify(partnerData));
  } catch (error) {
    const { mobileNo } = await getSessionData();
    removeQueryParam((window as any).location.href, REQ_DATA);
    logoutUser(mobileNo, { withPerpetual: false, withReload: false });
    throw new Error(`Error occurred while handling reqData: ${error}`);
  }
};

/**
 * Verifies the access token and initializes a user session if valid
 * @returns {Promise<void>}
 */
export const verifyAccessToken = async (): Promise<void> => {
  // Step 1: Get mobile number from localStorage or session
  const userMobileNo = await getMobileNumberFromStorage();

  if (!userMobileNo) {
    clearSession();
    clearPerpetualSession();
    return;
  }

  // Step 2: Proceed with verification and session initialization via connection manager
  try {
    await setSessionData("mobileNo", userMobileNo);
    const { access_token, age } = await verifySSOToken(userMobileNo);
    if (access_token) {
      await initLoginSession(userMobileNo);
    } else if (age) {
      store.dispatch(setLoginStatus({ loginStatus: "perpetual" }));
      await logoutUser(userMobileNo, { withPerpetual: false, withReload: false });
    }
  } catch (error) {
    console.error("Verification or session initialization failed:", error);
    try {
      await logoutUser(userMobileNo);
    } catch (logoutError) {
      console.error("Failed to logout user after verification error:", logoutError);
    }
  }
};
